/**
 * AnimToolbar — Controls, timeline, sidebar, frame/sheet property panels,
 * and UI helpers for the AnimationPreview editor.
 */

import Phaser from 'phaser';
import { CANVAS_WIDTH, CANVAS_HEIGHT } from '@/config/GameConfig';
import { createTechButton, drawTechRect, fillTechRect, TECH } from '@/ui/TechUI';
import type { AnimPreviewState } from './AnimPreviewState';
import type { PreviewPanel } from './PreviewPanel';
import type { SourcePanel } from './SourcePanel';
import {
  SIDEBAR_W, WORK_W, TIMELINE_H,
  FRAME_X, FRAME_SIZE,
} from './layoutConstants';

/**
 * Callbacks the AnimToolbar needs from the host scene.
 */
export interface AnimToolbarCallbacks {
  autoSave(): void;
  selectSheet(key: string): void;
  startPlayback(): void;
  stopPlayback(): void;
  showFrame(idx: number): void;
  reSliceTexture(): void;
}

export class AnimToolbar {
  private scene: Phaser.Scene;
  private state: AnimPreviewState;
  private cb: AnimToolbarCallbacks;
  private previewPanel: PreviewPanel;
  private sourcePanel: SourcePanel;

  // UI containers
  sidebarContainer!: Phaser.GameObjects.Container;
  timelineContainer!: Phaser.GameObjects.Container;
  controlsContainer!: Phaser.GameObjects.Container;
  framePropContainer!: Phaser.GameObjects.Container;
  sheetPropContainer!: Phaser.GameObjects.Container;

  // Scroll state
  private sidebarVisibleCount = 0;
  private sidebarScroll = 0;
  private timelineScrollX = 0;
  private timelineTotalW = 0;
  private timelineAvailW = 0;

  // Timeline drag-to-reorder
  private dragIdx = -1;
  private dragGhost: Phaser.GameObjects.Container | null = null;
  private dropIndicator: Phaser.GameObjects.Graphics | null = null;
  private timelineThumbW = 0;
  private timelineThumbH = 0;
  private timelineStartX = 0;
  private timelineOffsetX = 0;
  private timelineSpacing = 8;
  private timelineY = 0;

  // Sheet props grid info
  private gridInfoText: Phaser.GameObjects.Text | null = null;

  // Sidebar filter
  private filterInput: HTMLInputElement | null = null;
  private countLabel: Phaser.GameObjects.Text | null = null;

  constructor(
    scene: Phaser.Scene,
    state: AnimPreviewState,
    cb: AnimToolbarCallbacks,
    previewPanel: PreviewPanel,
    sourcePanel: SourcePanel,
  ) {
    this.scene = scene;
    this.state = state;
    this.cb = cb;
    this.previewPanel = previewPanel;
    this.sourcePanel = sourcePanel;
  }

  // ═══════════════════════════════════════════════════════
  // ── SIDEBAR: spritesheet list ──
  // ═══════════════════════════════════════════════════════

  buildSidebar(): void {
    const scene = this.scene;
    const SIDEBAR_LIST_TOP = 72;
    const bg = scene.add.graphics();
    fillTechRect(bg, 0, 0, SIDEBAR_W, CANVAS_HEIGHT, 0x111122, 0.95);
    drawTechRect(bg, 0, 0, SIDEBAR_W, CANVAS_HEIGHT, TECH.COLOR, 0.3);

    scene.add.text(SIDEBAR_W / 2, 8, 'SPRITESHEETS', {
      fontSize: '11px', fontFamily: 'monospace', fontStyle: 'bold', color: '#ffcc00',
    }).setOrigin(0.5, 0);

    // ── Search filter (HTML input overlaid on canvas) ──
    const canvas = scene.game.canvas;
    const canvasRect = canvas.getBoundingClientRect();
    const scaleX = canvasRect.width / (canvas.width || CANVAS_WIDTH);
    const scaleY = canvasRect.height / (canvas.height || CANVAS_HEIGHT);
    const inputGameX = 8;
    const inputGameY = 24;
    const inputGameW = SIDEBAR_W - 16;
    const inputGameH = 20;

    this.filterInput = document.createElement('input');
    this.filterInput.type = 'text';
    this.filterInput.placeholder = 'Filter...';
    this.filterInput.style.cssText = `
      position: fixed;
      left: ${canvasRect.left + inputGameX * scaleX}px;
      top: ${canvasRect.top + inputGameY * scaleY}px;
      width: ${inputGameW * scaleX}px;
      height: ${inputGameH * scaleY}px;
      font-family: monospace;
      font-size: ${10 * scaleY}px;
      color: #aaccee;
      background: #0a0a1a;
      border: 1px solid #334466;
      border-radius: 3px;
      outline: none;
      padding: 2px 6px;
      box-sizing: border-box;
      z-index: 9999;
    `;
    document.body.appendChild(this.filterInput);

    this.filterInput.addEventListener('input', () => {
      this.state.sidebarFilter = this.filterInput?.value ?? '';
      this.sidebarScroll = 0;
      this.rebuildSidebarList();
    });
    // Prevent Phaser from stealing keyboard while typing in the filter
    this.filterInput.addEventListener('focus', () => {
      const kb = scene.input.keyboard;
      if (kb) kb.enabled = false;
    });
    this.filterInput.addEventListener('blur', () => {
      const kb = scene.input.keyboard;
      if (kb) kb.enabled = true;
    });

    // Reposition input on canvas resize
    const reposition = () => {
      if (!this.filterInput) return;
      const r = canvas.getBoundingClientRect();
      const sx = r.width / (canvas.width || CANVAS_WIDTH);
      const sy = r.height / (canvas.height || CANVAS_HEIGHT);
      this.filterInput.style.left = `${r.left + inputGameX * sx}px`;
      this.filterInput.style.top = `${r.top + inputGameY * sy}px`;
      this.filterInput.style.width = `${inputGameW * sx}px`;
      this.filterInput.style.height = `${inputGameH * sy}px`;
      this.filterInput.style.fontSize = `${10 * sy}px`;
    };
    window.addEventListener('resize', reposition);

    // Cleanup on scene shutdown
    scene.events.once('shutdown', () => {
      this.filterInput?.remove();
      this.filterInput = null;
      window.removeEventListener('resize', reposition);
    });

    // ── Archive toggle + count (same line) ──
    const archiveLabel = scene.add.text(8, 50, '[ ] archived', {
      fontSize: '9px', fontFamily: 'monospace', color: '#666688',
    }).setOrigin(0, 0).setInteractive({ useHandCursor: true });
    const updateArchiveLabel = () => {
      archiveLabel.setText(this.state.showArchived ? '[x] archived' : '[ ] archived');
      archiveLabel.setColor(this.state.showArchived ? '#88aacc' : '#666688');
    };
    archiveLabel.on('pointerdown', () => {
      this.state.showArchived = !this.state.showArchived;
      updateArchiveLabel();
      if (this.state.showArchived) {
        this.loadArchivedSpritesheets();
      } else {
        this.rebuildSidebarList();
      }
    });
    updateArchiveLabel();

    this.countLabel = scene.add.text(SIDEBAR_W - 8, 50, '', {
      fontSize: '9px', fontFamily: 'monospace', color: '#556677',
    }).setOrigin(1, 0);

    this.sidebarContainer = scene.add.container(0, SIDEBAR_LIST_TOP);
    this.rebuildSidebarList();

    scene.input.on('wheel', (_ptr: Phaser.Input.Pointer, _gos: any[], _dx: number, dy: number) => {
      if (_ptr.x < SIDEBAR_W) {
        this.sidebarScroll = Phaser.Math.Clamp(
          this.sidebarScroll - dy * 0.5,
          Math.min(0, -(this.sidebarVisibleCount * 30 - (CANVAS_HEIGHT - SIDEBAR_LIST_TOP - 10))),
          0,
        );
        this.sidebarContainer.setY(SIDEBAR_LIST_TOP + this.sidebarScroll);
      } else if (_ptr.y > CANVAS_HEIGHT - TIMELINE_H) {
        const overflow = this.timelineTotalW - this.timelineAvailW;
        if (overflow > 0) {
          const delta = (_dx || 0) + dy;
          this.timelineScrollX = Phaser.Math.Clamp(
            this.timelineScrollX - delta * 1.5,
            -overflow,
            0,
          );
          this.timelineContainer.setX(this.timelineScrollX);
        }
      }
    });
  }

  /** Fetch archived spritesheets from dev server and load them into Phaser */
  private loadArchivedSpritesheets(): void {
    fetch('/api/archived-spritesheets')
      .then(r => r.json())
      .then((entries: { key: string; path: string; frameWidth: number; frameHeight: number }[]) => {
        let pending = 0;
        for (const entry of entries) {
          if (this.scene.textures.exists(entry.key)) {
            // Already loaded — just ensure it's in sheetKeys
            if (!this.state.sheetKeys.includes(entry.key)) {
              this.state.sheetKeys.push(entry.key);
            }
            continue;
          }
          pending++;
          const img = new Image();
          img.onload = () => {
            this.scene.textures.addSpriteSheet(entry.key, img, {
              frameWidth: entry.frameWidth,
              frameHeight: entry.frameHeight,
            });
            if (!this.state.sheetKeys.includes(entry.key)) {
              this.state.sheetKeys.push(entry.key);
            }
            pending--;
            if (pending === 0) {
              this.state.sheetKeys.sort();
              this.rebuildSidebarList();
            }
          };
          img.onerror = () => { pending--; };
          img.src = entry.path;
        }
        if (pending === 0) {
          this.state.sheetKeys.sort();
          this.rebuildSidebarList();
        }
      })
      .catch(() => this.rebuildSidebarList());
  }

  rebuildSidebarList(): void {
    const scene = this.scene;
    const st = this.state;
    this.sidebarContainer.removeAll(true);

    const isArchived = (k: string) => /_archived(_\d+)?$/i.test(k);
    let visibleKeys = st.showArchived
      ? st.sheetKeys
      : st.sheetKeys.filter(k => !isArchived(k));
    // Apply text filter
    if (st.sidebarFilter) {
      const q = st.sidebarFilter.toLowerCase();
      visibleKeys = visibleKeys.filter(k => k.toLowerCase().includes(q));
    }
    this.sidebarVisibleCount = visibleKeys.length;
    // Update count label
    if (this.countLabel) {
      const total = st.showArchived ? st.sheetKeys.length : st.sheetKeys.filter(k => !isArchived(k)).length;
      this.countLabel.setText(st.sidebarFilter ? `${visibleKeys.length}/${total}` : `${total}`);
    }

    visibleKeys.forEach((key, i) => {
      const y = i * 30;
      const tex = scene.textures.get(key);
      const fc = tex ? tex.frameTotal - 1 : 0;
      const isSelected = key === st.selectedSheet;

      const item = scene.add.container(0, y);

      const bg = scene.add.graphics();
      if (isSelected) {
        bg.fillStyle(0xffcc00, 0.15);
        bg.fillRect(4, -12, SIDEBAR_W - 8, 26);
      }
      item.add(bg);

      const archived = isArchived(key);
      const normalColor = archived ? '#666677' : '#aaaacc';
      const label = scene.add.text(12, 0, `${archived ? '📦 ' : ''}${key} (${fc})`, {
        fontSize: '10px', fontFamily: 'monospace',
        color: isSelected ? '#ffcc00' : normalColor,
      }).setOrigin(0, 0.5);
      item.add(label);

      const hit = scene.add.rectangle(SIDEBAR_W / 2, 0, SIDEBAR_W, 26, 0x000000, 0)
        .setInteractive({ useHandCursor: true });
      hit.on('pointerdown', () => this.cb.selectSheet(key));
      hit.on('pointerover', () => { if (!isSelected) label.setColor('#ffffff'); });
      hit.on('pointerout', () => { if (!isSelected) label.setColor(normalColor); });
      item.add(hit);

      this.sidebarContainer.add(item);
    });
  }

  // ═══════════════════════════════════════════════════════
  // ── TIMELINE ──
  // ═══════════════════════════════════════════════════════

  buildTimeline(): void {
    const scene = this.scene;
    const bg = scene.add.graphics();
    fillTechRect(bg, SIDEBAR_W, CANVAS_HEIGHT - TIMELINE_H, CANVAS_WIDTH - SIDEBAR_W, TIMELINE_H, 0x111122, 0.95);
    drawTechRect(bg, SIDEBAR_W, CANVAS_HEIGHT - TIMELINE_H, CANVAS_WIDTH - SIDEBAR_W, TIMELINE_H, TECH.COLOR, 0.3);

    scene.add.text(SIDEBAR_W + 10, CANVAS_HEIGHT - TIMELINE_H + 6, 'TIMELINE', {
      fontSize: '10px', fontFamily: 'monospace', fontStyle: 'bold', color: '#666688',
    });

    this.timelineContainer = scene.add.container(0, 0);

    // Drag-to-reorder handlers
    scene.input.on('drag', (_ptr: Phaser.Input.Pointer) => {
      if (this.dragIdx < 0 || !this.dragGhost || !this.dropIndicator) return;
      const tw = this.timelineThumbW;
      const th = this.timelineThumbH;
      this.dragGhost.setPosition(_ptr.x - tw / 2, this.timelineY);
      const relX = _ptr.x - this.timelineStartX - this.timelineScrollX;
      const slotW = tw + this.timelineSpacing;
      const dropIdx = Phaser.Math.Clamp(Math.round(relX / slotW), 0, this.state.frameCount);
      this.dropIndicator.clear();
      this.dropIndicator.lineStyle(3, 0xffcc00, 1);
      const lineX = this.timelineStartX + this.timelineScrollX + dropIdx * slotW - this.timelineSpacing / 2;
      this.dropIndicator.lineBetween(lineX, this.timelineY - 4, lineX, this.timelineY + th + 4);
      this.dropIndicator.strokePath();
    });

    scene.input.on('dragend', (_ptr: Phaser.Input.Pointer) => {
      if (this.dragIdx < 0) return;
      const fromIdx = this.dragIdx;
      const tw = this.timelineThumbW;
      const slotW = tw + this.timelineSpacing;
      const relX = _ptr.x - this.timelineStartX - this.timelineScrollX;
      let toIdx = Phaser.Math.Clamp(Math.round(relX / slotW), 0, this.state.frameCount - 1);
      if (this.dragGhost) { this.dragGhost.destroy(); this.dragGhost = null; }
      if (this.dropIndicator) { this.dropIndicator.clear(); }
      this.dragIdx = -1;

      if (fromIdx !== toIdx) {
        const order = this.state.animDef.frameOrder;
        const frames = this.state.animDef.frames;
        const [movedOrder] = order.splice(fromIdx, 1);
        const [movedFrame] = frames.splice(fromIdx, 1);
        order.splice(toIdx, 0, movedOrder);
        frames.splice(toIdx, 0, movedFrame);
        this.state.selectedFrame = toIdx;
        this.cb.autoSave();
      }

      this.refreshTimeline();
      this.refreshFrameProps();
    });
  }

  /** Auto-scroll timeline so the given frame index is visible */
  scrollTimelineToFrame(idx: number): void {
    if (this.timelineTotalW <= this.timelineAvailW) return;
    const slotW = this.timelineThumbW + this.timelineSpacing;
    const frameCenter = idx * slotW + slotW / 2;
    const viewCenter = this.timelineAvailW / 2;
    const overflow = this.timelineTotalW - this.timelineAvailW;
    this.timelineScrollX = Phaser.Math.Clamp(-(frameCenter - viewCenter), -overflow, 0);
    this.timelineContainer.setX(this.timelineScrollX);
  }

  refreshTimeline(): void {
    const scene = this.scene;
    const st = this.state;
    this.timelineContainer.removeAll(true);
    if (!st.selectedSheet || st.frameCount === 0) return;

    const startX = SIDEBAR_W + 20;
    const y = CANVAS_HEIGHT - TIMELINE_H + 30;
    const maxThumbH = 80;
    const spacing = 8;
    const availableW = CANVAS_WIDTH - SIDEBAR_W - 40;

    const fw = st.animDef.frameWidth || 1;
    const fh = st.animDef.frameHeight || 1;
    const aspect = fw / fh;
    const thumbH = Math.min(maxThumbH, TIMELINE_H - 60);
    const thumbW = Math.round(thumbH * aspect);

    const totalW = st.frameCount * (thumbW + spacing);
    this.timelineTotalW = totalW;
    this.timelineAvailW = availableW;
    const offsetX = totalW <= availableW ? (availableW - totalW) / 2 : 0;
    if (totalW <= availableW) {
      this.timelineScrollX = 0;
      this.timelineContainer.setX(0);
    } else {
      this.timelineScrollX = Phaser.Math.Clamp(this.timelineScrollX, -(totalW - availableW), 0);
      this.timelineContainer.setX(this.timelineScrollX);
    }

    this.timelineThumbW = thumbW;
    this.timelineThumbH = thumbH;
    this.timelineStartX = startX + offsetX;
    this.timelineOffsetX = offsetX;
    this.timelineSpacing = spacing;
    this.timelineY = y;

    const order = st.animDef.frameOrder;

    for (let i = 0; i < st.frameCount; i++) {
      const x = startX + offsetX + i * (thumbW + spacing);
      const sheetFrame = order[i];
      const frameDef = st.animDef.frames[i];
      const isSelected = i === st.selectedFrame;

      const container = scene.add.container(x, y);

      const bg = scene.add.graphics();
      if (isSelected) {
        bg.fillStyle(0xffcc00, 0.2);
        bg.fillRect(0, 0, thumbW, thumbH);
        drawTechRect(bg, 0, 0, thumbW, thumbH, 0xffcc00, 1, 2);
      } else {
        bg.fillStyle(0x1a1a2e, 0.8);
        bg.fillRect(0, 0, thumbW, thumbH);
        drawTechRect(bg, 0, 0, thumbW, thumbH, TECH.COLOR, 0.3);
      }
      container.add(bg);

      // Frame thumbnail
      const fDef = st.animDef.frames[i];
      const hasFBbox = fDef?.bboxW != null && fDef.bboxW > 0;
      const hasFAdj = hasFBbox || (fDef?.offsetX ?? 0) !== 0 || (fDef?.offsetY ?? 0) !== 0 || (fDef?.scale ?? 1) !== 1;
      let thumb: Phaser.GameObjects.Sprite;
      if (hasFAdj && fDef) {
        const bakedCanvas = this.previewPanel.bakeFrame(sheetFrame, fDef);
        const tKey = `__thumb_${i}_${sheetFrame}`;
        if (scene.textures.exists(tKey)) scene.textures.remove(tKey);
        scene.textures.addCanvas(tKey, bakedCanvas);
        thumb = scene.add.sprite(thumbW / 2, thumbH / 2, tKey);
      } else {
        thumb = scene.add.sprite(thumbW / 2, thumbH / 2, st.selectedSheet, sheetFrame);
      }
      const scale = Math.min((thumbW - 4) / thumb.frame.width, (thumbH - 4) / thumb.frame.height);
      thumb.setScale(scale);
      container.add(thumb);

      const label = sheetFrame !== i ? `${i} [${sheetFrame}]` : `${i}`;
      container.add(scene.add.text(thumbW / 2, thumbH + 4, label, {
        fontSize: '9px', fontFamily: 'monospace', color: isSelected ? '#ffcc00' : '#666688',
      }).setOrigin(0.5, 0));

      container.add(scene.add.text(thumbW / 2, thumbH + 16, `${frameDef.duration}ms`, {
        fontSize: '8px', fontFamily: 'monospace', color: '#44aa88',
      }).setOrigin(0.5, 0));

      // Hit area
      const hit = scene.add.rectangle(thumbW / 2, thumbH / 2, thumbW, thumbH, 0, 0)
        .setInteractive({ useHandCursor: true, draggable: true });
      let didDrag = false;
      hit.on('pointerdown', () => {
        didDrag = false;
        this.cb.stopPlayback();
        st.selectedFrame = i;
        this.cb.showFrame(i);
        this.refreshFrameProps();
      });
      hit.on('pointerup', () => {
        if (!didDrag) {
          this.refreshTimeline();
        }
      });

      hit.on('dragstart', () => {
        didDrag = true;
        this.dragIdx = i;
        this.dragGhost = scene.add.container(x, y).setAlpha(0.6).setDepth(100);
        const ghostBg = scene.add.graphics();
        ghostBg.fillStyle(0xffcc00, 0.3);
        ghostBg.fillRect(0, 0, thumbW, thumbH);
        drawTechRect(ghostBg, 0, 0, thumbW, thumbH, 0xffcc00, 0.8, 2);
        this.dragGhost.add(ghostBg);
        const ghostThumb = scene.add.sprite(thumbW / 2, thumbH / 2, st.selectedSheet, sheetFrame);
        ghostThumb.setScale(scale);
        this.dragGhost.add(ghostThumb);
        this.dropIndicator = scene.add.graphics().setDepth(99);
        container.setAlpha(0.3);
      });
      container.add(hit);

      // Per-frame action buttons (visible when selected)
      if (isSelected) {
        const delBtn = scene.add.text(thumbW - 2, -2, 'X', {
          fontSize: '9px', fontFamily: 'monospace', fontStyle: 'bold', color: '#ff4444',
          backgroundColor: '#1a1a2e', padding: { x: 3, y: 1 },
        }).setOrigin(1, 0).setInteractive({ useHandCursor: true });
        let confirmPending = false;
        let confirmTimeout: ReturnType<typeof setTimeout> | null = null;
        delBtn.on('pointerdown', (ptr: Phaser.Input.Pointer) => {
          ptr.event.stopPropagation();
          if (confirmPending) {
            if (confirmTimeout) clearTimeout(confirmTimeout);
            this.deleteFrame(i);
          } else {
            confirmPending = true;
            delBtn.setText('?');
            scene.tweens.add({ targets: delBtn, alpha: 0.3, duration: 200, yoyo: true, repeat: -1 });
            confirmTimeout = setTimeout(() => {
              confirmPending = false;
              delBtn.setText('X').setAlpha(1);
              scene.tweens.killTweensOf(delBtn);
            }, 2000);
          }
        });
        container.add(delBtn);

        const dupBtn = scene.add.text(2, -2, '+D', {
          fontSize: '9px', fontFamily: 'monospace', fontStyle: 'bold', color: '#44ff88',
          backgroundColor: '#1a1a2e', padding: { x: 3, y: 1 },
        }).setOrigin(0, 0).setInteractive({ useHandCursor: true });
        dupBtn.on('pointerdown', (ptr: Phaser.Input.Pointer) => {
          ptr.event.stopPropagation();
          this.duplicateFrame(i);
        });
        container.add(dupBtn);
      }

      this.timelineContainer.add(container);
    }
  }

  /** Delete frame at playback index */
  deleteFrame(idx: number): void {
    const st = this.state;
    if (st.frameCount <= 1) return;
    st.animDef.frames.splice(idx, 1);
    st.animDef.frameOrder.splice(idx, 1);
    st.frameCount = st.animDef.frameOrder.length;
    if (st.selectedFrame >= st.frameCount) {
      st.selectedFrame = st.frameCount - 1;
    }
    this.cb.autoSave();
    this.cb.showFrame(Math.max(0, st.selectedFrame));
    this.refreshTimeline();
    this.refreshFrameProps();
  }

  /** Duplicate frame at playback index */
  duplicateFrame(idx: number): void {
    const st = this.state;
    const srcFrame = st.animDef.frames[idx];
    const srcOrder = st.animDef.frameOrder[idx];
    st.animDef.frames.splice(idx + 1, 0, { ...srcFrame });
    st.animDef.frameOrder.splice(idx + 1, 0, srcOrder);
    st.frameCount = st.animDef.frameOrder.length;
    st.selectedFrame = idx + 1;
    this.cb.autoSave();
    this.cb.showFrame(st.selectedFrame);
    this.refreshTimeline();
    this.refreshFrameProps();
  }

  // ═══════════════════════════════════════════════════════
  // ── CONTROLS: play/pause/step/loop ──
  // ═══════════════════════════════════════════════════════

  buildControls(): void {
    this.controlsContainer = this.scene.add.container(SIDEBAR_W + WORK_W / 2, CANVAS_HEIGHT - TIMELINE_H - 45);
    this.refreshControls();
  }

  refreshControls(): void {
    const scene = this.scene;
    const st = this.state;
    this.controlsContainer.removeAll(true);

    const btnW = 36;
    const gap = 6;
    const totalBtns = 5;
    const startX = -(totalBtns * (btnW + gap)) / 2 + btnW / 2;

    // |< (first frame)
    this.controlsContainer.add(createTechButton(scene, startX, 0, '|<', {
      onClick: () => { this.cb.stopPlayback(); st.selectedFrame = 0; this.cb.showFrame(0); this.refreshTimeline(); this.refreshFrameProps(); },
      width: btnW, height: 24, fontSize: '10px',
    }));

    // < (prev frame)
    this.controlsContainer.add(createTechButton(scene, startX + (btnW + gap), 0, '<', {
      onClick: () => {
        this.cb.stopPlayback();
        st.selectedFrame = Math.max(0, (st.selectedFrame < 0 ? 0 : st.selectedFrame) - 1);
        this.cb.showFrame(st.selectedFrame);
        this.refreshTimeline(); this.refreshFrameProps();
      },
      width: btnW, height: 24, fontSize: '10px',
    }));

    // Play / Pause
    const playLabel = st.playing ? '||' : '>';
    this.controlsContainer.add(createTechButton(scene, startX + 2 * (btnW + gap), 0, playLabel, {
      onClick: () => {
        if (st.playing) this.cb.stopPlayback(); else this.cb.startPlayback();
        this.refreshControls();
      },
      width: btnW, height: 24, fontSize: '10px', color: st.playing ? TECH.DANGER : TECH.SUCCESS,
    }));

    // > (next frame)
    this.controlsContainer.add(createTechButton(scene, startX + 3 * (btnW + gap), 0, '>', {
      onClick: () => {
        this.cb.stopPlayback();
        st.selectedFrame = Math.min(st.frameCount - 1, (st.selectedFrame < 0 ? -1 : st.selectedFrame) + 1);
        this.cb.showFrame(st.selectedFrame);
        this.refreshTimeline(); this.refreshFrameProps();
      },
      width: btnW, height: 24, fontSize: '10px',
    }));

    // >| (last frame)
    this.controlsContainer.add(createTechButton(scene, startX + 4 * (btnW + gap), 0, '>|', {
      onClick: () => { this.cb.stopPlayback(); st.selectedFrame = st.frameCount - 1; this.cb.showFrame(st.selectedFrame); this.refreshTimeline(); this.refreshFrameProps(); },
      width: btnW, height: 24, fontSize: '10px',
    }));

    // Toggle row below transport controls
    const toggleY = 30;
    const toggleW = 80;
    const toggleGap = 6;
    const numToggles = 5;
    const toggleStartX = -((numToggles * toggleW + (numToggles - 1) * toggleGap) / 2) + toggleW / 2;

    // Loop toggle
    const loopLabel = st.animDef?.repeat === -1 ? 'LOOP: ON' : 'LOOP: OFF';
    this.controlsContainer.add(createTechButton(scene, toggleStartX, toggleY, loopLabel, {
      onClick: () => {
        if (!st.animDef) return;
        st.animDef.repeat = st.animDef.repeat === -1 ? 0 : -1;
        this.cb.autoSave();
        this.refreshControls();
      },
      width: toggleW, height: 22, fontSize: '9px', color: st.animDef?.repeat === -1 ? TECH.ACCENT : TECH.DIM,
    }));

    // Onion skin toggle
    const onionLabel = st.onionEnabled ? 'ONION: ON' : 'ONION: OFF';
    this.controlsContainer.add(createTechButton(scene, toggleStartX + toggleW + toggleGap, toggleY, onionLabel, {
      onClick: () => {
        st.onionEnabled = !st.onionEnabled;
        this.cb.showFrame(st.selectedFrame >= 0 ? st.selectedFrame : st.playFrameIdx);
        this.refreshControls();
      },
      width: toggleW, height: 22, fontSize: '9px', color: st.onionEnabled ? 0xff44ff : TECH.DIM,
    }));

    // Crosshair toggle
    const crossLabel = st.crosshairEnabled ? 'CENTER: ON' : 'CENTER: OFF';
    this.controlsContainer.add(createTechButton(scene, toggleStartX + 2 * (toggleW + toggleGap), toggleY, crossLabel, {
      onClick: () => {
        st.crosshairEnabled = !st.crosshairEnabled;
        this.previewPanel.drawCrosshair();
        this.refreshControls();
      },
      width: toggleW, height: 22, fontSize: '9px', color: st.crosshairEnabled ? 0x44ffaa : TECH.DIM,
    }));

    // Auto-fit
    this.controlsContainer.add(createTechButton(scene, toggleStartX + 3 * (toggleW + toggleGap), toggleY, 'AUTO-FIT', {
      onClick: () => {
        this.sourcePanel.autoFitFrames();
        this.cb.showFrame(st.selectedFrame >= 0 ? st.selectedFrame : 0);
        this.refreshTimeline();
        this.refreshFrameProps();
      },
      width: toggleW, height: 22, fontSize: '9px', color: TECH.SUCCESS,
    }));

    // Bake & Save
    this.controlsContainer.add(createTechButton(scene, toggleStartX + 4 * (toggleW + toggleGap), toggleY, 'BAKE&SAVE', {
      onClick: () => this.previewPanel.bakeAndSave(),
      width: toggleW, height: 22, fontSize: '9px', color: 0xff8844,
    }));
  }

  // ═══════════════════════════════════════════════════════
  // ── SHEET PROPERTIES ──
  // ═══════════════════════════════════════════════════════

  buildSheetProps(): void {
    this.sheetPropContainer = this.scene.add.container(CANVAS_WIDTH - 140, 50);
  }

  refreshSheetProps(): void {
    const scene = this.scene;
    const st = this.state;
    this.sheetPropContainer.removeAll(true);
    if (!st.animDef) return;

    const x = 0;
    let y = 0;

    this.sheetPropContainer.add(
      scene.add.text(x, y, 'FRAME SIZE', {
        fontSize: '11px', fontFamily: 'monospace', fontStyle: 'bold', color: '#ffcc00',
      }).setOrigin(0.5, 0)
    );
    y += 22;

    const tex = scene.textures.get(st.selectedSheet);
    const source = tex?.getSourceImage();
    const imgW = source?.width ?? 0;
    const imgH = source?.height ?? 0;

    this.sheetPropContainer.add(
      scene.add.text(x, y, `image: ${imgW}×${imgH}`, {
        fontSize: '8px', fontFamily: 'monospace', color: '#666688',
      }).setOrigin(0.5, 0)
    );
    y += 18;

    // frameWidth
    this.sheetPropContainer.add(
      scene.add.text(x, y, 'Width', {
        fontSize: '9px', fontFamily: 'monospace', color: '#888899',
      }).setOrigin(0.5, 0)
    );
    y += 14;
    this.addNumberControlTo(this.sheetPropContainer, x, y, st.animDef.frameWidth, 8, imgW || 512, 1, (v) => {
      st.animDef.frameWidth = v;
      this.cb.reSliceTexture();
      this.updateGridInfo(imgW, imgH);
    });
    y += 30;

    // frameHeight
    this.sheetPropContainer.add(
      scene.add.text(x, y, 'Height', {
        fontSize: '9px', fontFamily: 'monospace', color: '#888899',
      }).setOrigin(0.5, 0)
    );
    y += 14;
    this.addNumberControlTo(this.sheetPropContainer, x, y, st.animDef.frameHeight, 8, imgH || 512, 1, (v) => {
      st.animDef.frameHeight = v;
      this.cb.reSliceTexture();
      this.updateGridInfo(imgW, imgH);
    });
    y += 30;

    this.gridInfoText = scene.add.text(x, y, '', {
      fontSize: '9px', fontFamily: 'monospace', color: '#44aa88',
    }).setOrigin(0.5, 0);
    this.sheetPropContainer.add(this.gridInfoText);
    this.updateGridInfo(imgW, imgH);
  }

  private updateGridInfo(imgW: number, imgH: number): void {
    if (!this.gridInfoText || !this.state.animDef) return;
    const cols = this.state.animDef.frameWidth > 0 ? Math.floor(imgW / this.state.animDef.frameWidth) : 0;
    const rows = this.state.animDef.frameHeight > 0 ? Math.floor(imgH / this.state.animDef.frameHeight) : 0;
    this.gridInfoText.setText(`grid: ${cols}×${rows} = ${cols * rows} frames`);
  }

  // ═══════════════════════════════════════════════════════
  // ── FRAME PROPERTIES ──
  // ═══════════════════════════════════════════════════════

  buildFrameProps(): void {
    this.framePropContainer = this.scene.add.container(CANVAS_WIDTH - 140, 250);
  }

  refreshFrameProps(): void {
    const scene = this.scene;
    const st = this.state;
    this.framePropContainer.removeAll(true);
    if (st.selectedFrame < 0 || !st.animDef) return;

    const frameDef = st.animDef.frames[st.selectedFrame];
    if (!frameDef) return;

    const x = 0;
    let y = 0;

    const sep = scene.add.graphics();
    sep.lineStyle(1, TECH.COLOR, 0.2);
    sep.lineBetween(-60, y - 8, 60, y - 8);
    this.framePropContainer.add(sep);

    this.framePropContainer.add(
      scene.add.text(x, y, `FRAME ${st.selectedFrame}`, {
        fontSize: '11px', fontFamily: 'monospace', fontStyle: 'bold', color: '#ffcc00',
      }).setOrigin(0.5, 0)
    );
    y += 24;

    // Duration
    this.framePropContainer.add(
      scene.add.text(x, y, 'Duration (ms)', {
        fontSize: '9px', fontFamily: 'monospace', color: '#888899',
      }).setOrigin(0.5, 0)
    );
    y += 14;
    this.addNumberControlTo(this.framePropContainer, x, y, frameDef.duration, 10, 2000, 10, (v) => {
      frameDef.duration = v;
      this.cb.autoSave();
      this.refreshTimeline();
    });
    y += 28;

    const hasBbox = frameDef.bboxW != null && frameDef.bboxW > 0;

    if (hasBbox) {
      this.framePropContainer.add(
        scene.add.text(x, y, 'CROP BOX', {
          fontSize: '9px', fontFamily: 'monospace', fontStyle: 'bold', color: '#00ffaa',
        }).setOrigin(0.5, 0)
      );
      y += 18;

      const refreshBbox = () => {
        this.cb.autoSave();
        this.cb.showFrame(st.selectedFrame);
        this.refreshFrameProps();
      };

      // X
      this.framePropContainer.add(scene.add.text(x, y, 'X', {
        fontSize: '9px', fontFamily: 'monospace', color: '#888899',
      }).setOrigin(0.5, 0));
      y += 12;
      this.addNumberControlTo(this.framePropContainer, x, y, frameDef.bboxX ?? 0, -500, 500, 1, (v) => {
        frameDef.bboxX = v; refreshBbox();
      });
      this.framePropContainer.add(this.addKeyHint(x + 90, y + 2, ['Sh', '<', '>']));
      y += 24;

      // Y
      this.framePropContainer.add(scene.add.text(x, y, 'Y', {
        fontSize: '9px', fontFamily: 'monospace', color: '#888899',
      }).setOrigin(0.5, 0));
      y += 12;
      this.addNumberControlTo(this.framePropContainer, x, y, frameDef.bboxY ?? 0, -500, 500, 1, (v) => {
        frameDef.bboxY = v; refreshBbox();
      });
      this.framePropContainer.add(this.addKeyHint(x + 90, y + 2, ['Sh', '^', 'v']));
      y += 24;

      // W
      this.framePropContainer.add(scene.add.text(x, y, 'W', {
        fontSize: '9px', fontFamily: 'monospace', color: '#888899',
      }).setOrigin(0.5, 0));
      y += 12;
      this.addNumberControlTo(this.framePropContainer, x, y, frameDef.bboxW!, 8, 1000, 1, (v) => {
        frameDef.bboxW = v; refreshBbox();
      });
      this.framePropContainer.add(this.addKeyHint(x + 90, y + 2, ['Cmd', '<', '>']));
      y += 24;

      // H
      this.framePropContainer.add(scene.add.text(x, y, 'H', {
        fontSize: '9px', fontFamily: 'monospace', color: '#888899',
      }).setOrigin(0.5, 0));
      y += 12;
      this.addNumberControlTo(this.framePropContainer, x, y, frameDef.bboxH!, 8, 1000, 1, (v) => {
        frameDef.bboxH = v; refreshBbox();
      });
      this.framePropContainer.add(this.addKeyHint(x + 90, y + 2, ['Cmd', '^', 'v']));
      y += 28;

      // Clear bbox
      this.framePropContainer.add(createTechButton(scene, x, y, 'CLEAR BBOX', {
        onClick: () => {
          frameDef.bboxX = undefined;
          frameDef.bboxY = undefined;
          frameDef.bboxW = undefined;
          frameDef.bboxH = undefined;
          this.cb.autoSave();
          this.cb.showFrame(st.selectedFrame);
          this.refreshFrameProps();
        },
        width: 90, height: 20, fontSize: '8px', color: TECH.DANGER,
      }));
      y += 28;
    } else {
      // Legacy mode — offset/scale
      this.framePropContainer.add(
        scene.add.text(x, y, 'Offset X', {
          fontSize: '9px', fontFamily: 'monospace', color: '#888899',
        }).setOrigin(0.5, 0)
      );
      y += 14;
      this.addNumberControlTo(this.framePropContainer, x, y, frameDef.offsetX, -50, 50, 1, (v) => {
        frameDef.offsetX = v;
        this.cb.autoSave();
        this.cb.showFrame(st.selectedFrame);
      });
      this.framePropContainer.add(this.addKeyHint(x + 90, y + 2, ['Sh', '<', '>']));
      y += 28;

      this.framePropContainer.add(
        scene.add.text(x, y, 'Offset Y', {
          fontSize: '9px', fontFamily: 'monospace', color: '#888899',
        }).setOrigin(0.5, 0)
      );
      y += 14;
      this.addNumberControlTo(this.framePropContainer, x, y, frameDef.offsetY, -50, 50, 1, (v) => {
        frameDef.offsetY = v;
        this.cb.autoSave();
        this.cb.showFrame(st.selectedFrame);
      });
      this.framePropContainer.add(this.addKeyHint(x + 90, y + 2, ['Sh', '^', 'v']));
      y += 28;

      this.framePropContainer.add(
        scene.add.text(x, y, 'Scale', {
          fontSize: '9px', fontFamily: 'monospace', color: '#888899',
        }).setOrigin(0.5, 0)
      );
      y += 14;
      if (frameDef.scale === undefined) frameDef.scale = 1;
      this.addNumberControlTo(this.framePropContainer, x, y, Math.round(frameDef.scale * 100), 10, 300, 5, (v) => {
        frameDef.scale = v / 100;
        this.cb.autoSave();
        this.cb.showFrame(st.selectedFrame);
      }, '%');
      this.framePropContainer.add(this.addKeyHint(x + 90, y + 2, ['Cmd', '^', 'v']));
      y += 30;
    }

    // Apply duration to ALL frames
    let applyConfirm = false;
    let applyTimeout: ReturnType<typeof setTimeout> | null = null;
    const applyBtn = createTechButton(scene, x, y, 'APPLY TO ALL', {
      onClick: () => {
        if (applyConfirm) {
          if (applyTimeout) clearTimeout(applyTimeout);
          const dur = frameDef.duration;
          st.animDef.frames.forEach(f => f.duration = dur);
          this.cb.autoSave();
          this.refreshTimeline();
          this.refreshFrameProps();
        } else {
          applyConfirm = true;
          const txt = applyBtn.getAt(1) as Phaser.GameObjects.Text;
          txt.setText('CONFIRM?');
          txt.setColor('#ff4444');
          applyTimeout = setTimeout(() => {
            applyConfirm = false;
            txt.setText('APPLY TO ALL');
            txt.setColor('#ffcc00');
          }, 2000);
        }
      },
      width: 110, height: 22, fontSize: '9px', color: TECH.ACCENT,
    });
    this.framePropContainer.add(applyBtn);
  }

  // ═══════════════════════════════════════════════════════
  // ── UI Helpers ──
  // ═══════════════════════════════════════════════════════

  /** Reusable [ - ] value [ + ] control with hold-to-repeat */
  addNumberControlTo(
    container: Phaser.GameObjects.Container,
    cx: number, cy: number,
    value: number, min: number, max: number, step: number,
    onChange: (v: number) => void,
    suffix = '',
  ): void {
    const scene = this.scene;
    const valText = scene.add.text(cx, cy, `${value}${suffix}`, {
      fontSize: '12px', fontFamily: 'monospace', fontStyle: 'bold', color: '#ffffff',
    }).setOrigin(0.5, 0);
    container.add(valText);

    const applyDelta = (delta: number) => {
      const nv = Phaser.Math.Clamp(value + delta, min, max);
      if (nv !== value) { value = nv; valText.setText(`${value}${suffix}`); onChange(value); }
    };

    const holdRepeat = (delta: number, hitArea: Phaser.GameObjects.Rectangle) => {
      let timer: ReturnType<typeof setTimeout> | null = null;
      let interval: ReturnType<typeof setInterval> | null = null;
      const stopRepeat = () => {
        if (timer) { clearTimeout(timer); timer = null; }
        if (interval) { clearInterval(interval); interval = null; }
      };
      hitArea.on('pointerdown', () => {
        applyDelta(delta);
        timer = setTimeout(() => {
          interval = setInterval(() => applyDelta(delta), 80);
        }, 350);
      });
      hitArea.on('pointerup', stopRepeat);
      hitArea.on('pointerout', stopRepeat);
    };

    const minusBtn = createTechButton(scene, cx - 50, cy + 2, '-', {
      onClick: () => {},
      width: 24, height: 20, fontSize: '11px',
    });
    container.add(minusBtn);
    const minusHit = minusBtn.getAt(minusBtn.length - 1) as Phaser.GameObjects.Rectangle;
    holdRepeat(-step, minusHit);

    const plusBtn = createTechButton(scene, cx + 50, cy + 2, '+', {
      onClick: () => {},
      width: 24, height: 20, fontSize: '11px',
    });
    container.add(plusBtn);
    const plusHit = plusBtn.getAt(plusBtn.length - 1) as Phaser.GameObjects.Rectangle;
    holdRepeat(step, plusHit);
  }

  /** Draw a row of small key-cap styled boxes */
  addKeyHint(cx: number, cy: number, keys: string[]): Phaser.GameObjects.Container {
    const scene = this.scene;
    const container = scene.add.container(cx, cy);
    const gap = 3;
    const padX = 4;
    const h = 14;
    const widths = keys.map(k => {
      const tw = k.length * 6 + padX * 2;
      return Math.max(16, tw);
    });
    const totalW = widths.reduce((a, b) => a + b, 0) + gap * (keys.length - 1);
    let x = -totalW / 2;

    for (let i = 0; i < keys.length; i++) {
      const w = widths[i];
      const gfx = scene.add.graphics();
      gfx.fillStyle(0x333344, 0.8);
      gfx.fillRoundedRect(x, -h / 2, w, h, 3);
      gfx.lineStyle(1, 0x555566, 0.6);
      gfx.strokeRoundedRect(x, -h / 2, w, h, 3);
      container.add(gfx);
      const label = scene.add.text(x + w / 2, 0, keys[i], {
        fontSize: '7px', fontFamily: 'monospace', fontStyle: 'bold', color: '#999999',
      }).setOrigin(0.5);
      container.add(label);
      x += w + gap;
    }
    return container;
  }
}
